import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Sparkles, Shield, Zap, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { getUserId, fetchUserStatus, clearStatusCache, updateUserPremiumStatus, getAuthUser } from "@/lib/subscription";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICING = {
  decision_partner: { INR: "₹99", USD: "$3" },
  enterprise: { INR: "₹999", USD: "$12" }
};

const detectDefaultCurrency = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const lang = navigator.language || "";
    if (tz.startsWith("Asia/Kolkata") || tz.startsWith("Asia/Calcutta") || lang.startsWith("hi") || lang === "en-IN") {
      return "INR";
    }
  } catch (e) { /* fallback to USD */ }
  return "USD";
};

const fadeIn = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 }
};

export default function PricingPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [currency, setCurrency] = useState(() => detectDefaultCurrency());
  const [enterpriseTrialAvailable, setEnterpriseTrialAvailable] = useState(false);

  useEffect(() => {
    fetchUserStatus(true).then(setUserStatus);
    // Check enterprise trial status
    const userId = getUserId();
    axios.get(`${API}/enterprise/trial-status/${userId}`)
      .then(res => setEnterpriseTrialAvailable(res.data.trial_available))
      .catch(() => {});
  }, []);

  const currentTier = userStatus?.plan || userStatus?.tier || 'free';
  const signedIn = !!getAuthUser();
  const isPremiumActive = signedIn && userStatus?.limit_type === 'daily' && currentTier !== 'free';
  const isEnterprise = currentTier === 'enterprise' && isPremiumActive;
  const isDecisionPartner = currentTier === 'decision_partner' && isPremiumActive;

  const handleContinueFree = () => {
    navigate("/");
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        // Wait a tick for Razorpay to initialize
        setTimeout(() => resolve(!!window.Razorpay), 100);
      };
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleUnlock = async (packageId) => {
    setIsLoading(packageId);
    
    try {
      // Step 1: Load Razorpay script
      const loaded = await loadRazorpayScript();
      if (!loaded || !window.Razorpay) {
        console.error("[Decisora] Razorpay script failed to load");
        toast.error("Payment system unavailable. Please disable ad-blocker and try again.");
        setIsLoading(null);
        return;
      }

      // Step 2: Create order
      let orderResponse;
      try {
        orderResponse = await axios.post(`${API}/payments/create-order`, {
          package_id: packageId,
          currency: currency
        });
      } catch (apiErr) {
        console.error("[Decisora] Order creation failed:", apiErr?.response?.data || apiErr.message);
        toast.error("Could not create payment order. Please try again.");
        setIsLoading(null);
        return;
      }
      
      const { order_id, amount, currency: orderCurrency, key_id, package_name } = orderResponse.data;
      const userId = getUserId();

      // Step 3: Open Razorpay checkout
      const options = {
        key: key_id,
        amount: amount,
        currency: orderCurrency,
        name: "Decisora",
        description: `${package_name} - Monthly Access`,
        order_id: order_id,
        // Enable PayPal for USD orders
        ...(orderCurrency === "USD" && {
          config: {
            display: {
              blocks: {
                international: {
                  name: "Pay with PayPal",
                  instruments: [{ method: "paypal" }]
                }
              },
              sequence: ["block.international"],
              preferences: { show_default_blocks: true }
            }
          }
        }),
        handler: async (response) => {
          // Verify payment
          try {
            const verifyResponse = await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              package_id: packageId,
              user_id: userId
            });
            
            if (verifyResponse.data.success) {
              updateUserPremiumStatus(packageId);
              toast.success("Payment successful! Features unlocked.");
              navigate("/payment/success");
            } else {
              toast.error("Payment verification failed");
            }
          } catch (verifyErr) {
            console.error("[Decisora] Payment verify failed:", verifyErr?.response?.data || verifyErr.message);
            toast.error("Payment verification failed. Please contact support.");
          }
          setIsLoading(null);
        },
        prefill: {
          ...(getAuthUser()?.email && { email: getAuthUser().email })
        },
        theme: {
          color: "#10b981"
        },
        modal: {
          ondismiss: () => {
            setIsLoading(null);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        console.error("[Decisora] Payment failed:", resp.error);
        toast.error(resp.error?.description || "Payment failed. Please try again.");
        setIsLoading(null);
      });
      rzp.open();
    } catch (err) {
      console.error("[Decisora] Payment init error:", err);
      toast.error("Unable to start payment. Please try again.");
      setIsLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center">
          <button
            onClick={() => navigate("/")}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        </div>
      </header>

      <main className="px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeIn}
            className="text-center mb-12"
          >
            <h1 className="font-heading text-3xl md:text-4xl text-foreground mb-3">
              Select how you want to analyze your decision
            </h1>
            <p className="text-muted-foreground">
              Start free. Upgrade when you need deeper clarity.
            </p>

            {/* Currency Toggle */}
            <div className="flex items-center justify-center gap-2 mt-4" data-testid="currency-toggle">
              <Globe className="h-3.5 w-3.5 text-muted-foreground" />
              <div className="inline-flex items-center rounded-full border border-border bg-secondary/30 p-0.5">
                <button
                  onClick={() => setCurrency("INR")}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    currency === "INR"
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="currency-inr"
                >
                  ₹ INR
                </button>
                <button
                  onClick={() => setCurrency("USD")}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    currency === "USD"
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="currency-usd"
                >
                  $ USD
                </button>
              </div>
            </div>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-4">
            {/* Free Tier */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="floating-card p-6 flex flex-col"
            >
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span className="text-xs font-medium text-primary uppercase tracking-wider">
                    Explore
                  </span>
                </div>
                <h3 className="font-heading text-2xl text-foreground mb-1">Free</h3>
                <p className="text-sm text-muted-foreground">
                  Try how Decisora thinks
                </p>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                <li className="flex items-start gap-2 text-sm text-foreground">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  Make decisions instantly
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  Clear structured summaries
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  No signup required
                </li>
              </ul>

              <Button
                onClick={handleContinueFree}
                variant="outline"
                className="w-full h-11 rounded-full"
              >
                Continue Free
              </Button>
            </motion.div>

            {/* Decision Partner */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glow-card p-6 flex flex-col relative"
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary text-primary-foreground">
                  Recommended
                </span>
              </div>

              <div className="mb-6 pt-2">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="h-4 w-4 text-primary" />
                  <span className="text-xs font-medium text-primary uppercase tracking-wider">
                    Decision Partner
                  </span>
                </div>
                <div className="flex items-baseline gap-1">
                  <h3 className="font-heading text-2xl text-foreground">{PRICING.decision_partner[currency]}</h3>
                  <span className="text-sm text-muted-foreground">/ month</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Think with confidence
                </p>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                <li className="flex items-start gap-2 text-sm text-foreground">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  1 trial decision free
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  2 decisions per week
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  Challenge My Choice
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  Deep Reasoning mode
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  Cancel anytime
                </li>
                {currency === "USD" && (
                  <li className="flex items-start gap-2 text-sm text-primary/80">
                    <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    PayPal supported
                  </li>
                )}
              </ul>

              {isDecisionPartner ? (
                <Button
                  variant="outline"
                  className="w-full h-11 rounded-full bg-primary/10 border-primary cursor-default"
                  disabled
                  data-testid="pricing-partner-current"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Current Plan
                </Button>
              ) : (
                <Button
                  onClick={() => handleUnlock("decision_partner")}
                  disabled={isLoading === "decision_partner"}
                  className="w-full h-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="pricing-partner-upgrade"
                >
                  {isLoading === "decision_partner" ? (
                    <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Upgrade"
                  )}
                </Button>
              )}
            </motion.div>

            {/* Enterprise */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="floating-card p-6 flex flex-col"
            >
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Enterprise
                  </span>
                </div>
                <div className="flex items-baseline gap-1">
                  <h3 className="font-heading text-2xl text-foreground">{PRICING.enterprise[currency]}</h3>
                  <span className="text-sm text-muted-foreground">/ month</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  For teams & heavy usage
                </p>
              </div>

              <ul className="space-y-3 mb-6 flex-1">
                <li className="flex items-start gap-2 text-sm text-primary font-medium">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  1 Free Enterprise Trial
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground">
                  <Check className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  Everything in Decision Partner
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground">
                  <Check className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  Advanced analysis
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground">
                  <Check className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  Priority processing
                </li>
                {currency === "USD" && (
                  <li className="flex items-start gap-2 text-sm text-primary/80">
                    <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    PayPal supported
                  </li>
                )}
              </ul>

              {/* Enterprise Safety Notice */}
              <div className="mb-4 p-3 rounded-lg bg-secondary/30 border border-border/50">
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Decisora supports thoughtful decision-making. Outcomes depend on human judgment and real-world factors. This is not legal, medical, or financial advice.
                </p>
              </div>

              {isEnterprise ? (
                <Button
                  variant="outline"
                  className="w-full h-11 rounded-full bg-primary/10 border-primary cursor-default"
                  disabled
                  data-testid="pricing-enterprise-current"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Current Plan
                </Button>
              ) : enterpriseTrialAvailable ? (
                <Button
                  onClick={() => navigate("/enterprise")}
                  className="w-full h-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="pricing-enterprise-trial"
                >
                  Start Free Trial
                </Button>
              ) : (
                <Button
                  onClick={() => handleUnlock("enterprise")}
                  disabled={isLoading === "enterprise"}
                  variant="outline"
                  className="w-full h-11 rounded-full"
                  data-testid="pricing-enterprise-upgrade"
                >
                  {isLoading === "enterprise" ? (
                    <div className="w-4 h-4 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
                  ) : (
                    `Upgrade ${PRICING.enterprise[currency]}/month`
                  )}
                </Button>
              )}
            </motion.div>
          </div>

          {/* Footer note */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-center text-xs text-muted-foreground mt-8"
          >
            Secure payment{currency === "USD" ? " · PayPal available" : ""} · No auto-renew · Cancel anytime
          </motion.p>
        </div>
      </main>

      {/* Disclaimer Footer */}
      <footer className="py-6 px-6">
        <p className="text-[11px] text-muted-foreground/50 text-center">
          Decisora provides structured thinking tools, not professional advice.
        </p>
      </footer>
    </div>
  );
}
